import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Lock, Loader2, Eye, EyeOff, CheckCircle } from "lucide-react";

interface ChangePasswordModalProps {
  isOpen: boolean;
  isFirstLogin: boolean;
  onChangePassword: (newPassword: string, currentPassword?: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  onClose?: () => void;
}

export function ChangePasswordModal({ 
  isOpen, 
  isFirstLogin, 
  onChangePassword,
  onClose 
}: ChangePasswordModalProps) {
  const { t } = useTranslation('auth');
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordRequirements = [
    { met: newPassword.length >= 8, text: t('changePassword.requirements.minLength') },
    { met: /[A-Z]/.test(newPassword), text: t('changePassword.requirements.uppercase') },
    { met: /[a-z]/.test(newPassword), text: t('changePassword.requirements.lowercase') },
    { met: /[0-9]/.test(newPassword), text: t('changePassword.requirements.digit') },
  ];

  const allRequirementsMet = passwordRequirements.every(r => r.met);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!allRequirementsMet) {
      setError(t('changePassword.errorRequirements'));
      return;
    }

    if (!passwordsMatch) {
      setError(t('changePassword.errorMismatch'));
      return;
    }

    setIsLoading(true);

    const result = await onChangePassword(
      newPassword, 
      isFirstLogin ? undefined : currentPassword
    );

    if (!result.success) {
      setError(result.error || t('changePassword.errorGeneric'));
    }

    setIsLoading(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isFirstLogin && onClose) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent 
        className="sm:max-w-md"
        onPointerDownOutside={(e) => {
          if (isFirstLogin) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isFirstLogin) e.preventDefault();
        }}
      >
        <DialogHeader>
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center">
            {isFirstLogin ? t('changePassword.titleFirst') : t('changePassword.titleRegular')}
          </DialogTitle>
          <DialogDescription className="text-center">
            {isFirstLogin 
              ? t('changePassword.descriptionFirst')
              : t('changePassword.descriptionRegular')
            }
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {!isFirstLogin && (
            <div className="space-y-2">
              <Label htmlFor="current-password">{t('changePassword.currentPassword')}</Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value);
                    setError("");
                  }}
                  placeholder="••••••••"
                  disabled={isLoading}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="new-password">{t('changePassword.newPassword')}</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setError("");
                }}
                placeholder="••••••••"
                disabled={isLoading}
                required
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-1 text-xs mt-2">
              {passwordRequirements.map((req, i) => (
                <div 
                  key={i} 
                  className={`flex items-center gap-1 ${req.met ? 'text-green-600' : 'text-muted-foreground'}`}
                >
                  <CheckCircle className={`h-3 w-3 ${req.met ? 'opacity-100' : 'opacity-30'}`} />
                  {req.text}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">{t('changePassword.confirmPassword')}</Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError("");
                }}
                placeholder="••••••••"
                disabled={isLoading}
                required
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
            {confirmPassword && !passwordsMatch && (
              <p className="text-xs text-destructive">{t('changePassword.errorMismatch')}</p>
            )}
            {passwordsMatch && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" /> {t('changePassword.passwordsMatch')}
              </p>
            )}
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <Button 
            type="submit" 
            className="w-full" 
            disabled={isLoading || !allRequirementsMet || !passwordsMatch}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('changePassword.saving')}
              </>
            ) : (
              t('changePassword.submit')
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
